export interface MotionSample {
  x: number
  y: number
  z: number
  /** True when the sensor already removed gravity (DeviceMotionEvent.acceleration). */
  gravityFree: boolean
  /** Milliseconds. */
  t: number
}

type Axis = 'x' | 'y' | 'z'

// A jolt is the phone's linear acceleration passing JOLT m/s² (~1.5 g) along
// the shake's axis; each change of direction makes one, so a back-and-forth is
// two. Tuned high on purpose: festival-goers dance with their phones.
const JOLT = 15
const JOLTS_TO_SHAKE = 4 // two back-and-forths
const MAX_JOLT_GAP_MS = 400
const COOLDOWN_MS = 3000
const GRAVITY_SMOOTHING = 0.8

/**
 * Feeds motion samples one at a time; returns true on the sample that
 * completes a shake. Rate-independent: it counts direction reversals, not
 * sample-to-sample deltas, so 60 Hz and 100 Hz sensors behave the same.
 */
export function createShakeDetector(): (sample: MotionSample) => boolean {
  let gravity: { x: number; y: number; z: number } | null = null
  let axis: Axis | null = null
  let lastSign = 0
  let lastJoltAt = -Infinity
  let jolts = 0
  let quietUntil = -Infinity

  return sample => {
    let v = { x: sample.x, y: sample.y, z: sample.z }
    if (!sample.gravityFree) {
      // Low-pass for gravity, then subtract it: what's left is the shake.
      gravity = gravity
        ? {
            x: GRAVITY_SMOOTHING * gravity.x + (1 - GRAVITY_SMOOTHING) * v.x,
            y: GRAVITY_SMOOTHING * gravity.y + (1 - GRAVITY_SMOOTHING) * v.y,
            z: GRAVITY_SMOOTHING * gravity.z + (1 - GRAVITY_SMOOTHING) * v.z,
          }
        : v
      v = { x: v.x - gravity.x, y: v.y - gravity.y, z: v.z - gravity.z }
    }

    if (sample.t - lastJoltAt > MAX_JOLT_GAP_MS) {
      axis = null
      jolts = 0
    }
    const along = axis ?? (['x', 'y', 'z'] as const).reduce((a, b) => (Math.abs(v[b]) > Math.abs(v[a]) ? b : a))
    const value = v[along]
    if (Math.abs(value) < JOLT) return false
    const sign = Math.sign(value)
    if (axis && sign === lastSign) return false // still the same push

    axis = along
    lastSign = sign
    lastJoltAt = sample.t
    jolts += 1
    if (jolts < JOLTS_TO_SHAKE || sample.t < quietUntil) return false
    jolts = 0
    axis = null
    quietUntil = sample.t + COOLDOWN_MS
    return true
  }
}
